/**
 * API client service with token management
 */
import axios from 'axios';
import { logApiError } from '../utils/errorLogger';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// Create axios instance
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management - initialize from localStorage
let accessToken = null;
let refreshToken = null;

// Initialize tokens from localStorage on module load
if (typeof window !== 'undefined') {
  accessToken = localStorage.getItem('accessToken');
  refreshToken = localStorage.getItem('refreshToken');
}

/**
 * Set authentication tokens
 * @param {string} token - Access token
 * @param {string} refresh - Refresh token
 */
export function setTokens(token, refresh) {
  accessToken = token;
  refreshToken = refresh;
  localStorage.setItem('accessToken', token);
  localStorage.setItem('refreshToken', refresh);
}

/**
 * Clear authentication tokens
 */
export function clearTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
}

/**
 * Get current access token
 * @returns {string|null}
 */
export function getAccessToken() {
  // Always check localStorage for the latest token
  const token = localStorage.getItem('accessToken');
  if (token && token !== accessToken) {
    accessToken = token;
  }
  return accessToken;
}

/**
 * Get current refresh token
 * @returns {string|null}
 */
export function getRefreshToken() {
  // Always check localStorage for the latest token
  const token = localStorage.getItem('refreshToken');
  if (token && token !== refreshToken) {
    refreshToken = token;
  }
  return refreshToken;
}

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    // Always get fresh token from localStorage to handle cross-tab updates
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // If no token, remove Authorization header
      delete config.headers.Authorization;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Log API errors
    logApiError(error, originalRequest);

    // Skip refresh for login/refresh endpoints
    if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh')) {
      return Promise.reject(error);
    }

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refresh = getRefreshToken();
      if (!refresh) {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
          refreshToken: refresh,
        });

        const { token, refreshToken: newRefreshToken } = response.data;
        setTokens(token, newRefreshToken);
        
        apiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
        originalRequest.headers.Authorization = `Bearer ${token}`;
        
        processQueue(null, token);
        isRefreshing = false;
        
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
